/**
 * External Switch Input Hook
 * Listens for custom-mapped switch inputs and triggers scanning actions
 */

import { useEffect, useCallback } from 'react';
import { getUserSettings, ExternalSwitchMapping } from '@/lib/userSettings';
import { useInputMethod } from '@/contexts/InputMethodContext';
import { InputMethod } from '@/lib/inputMethods';

export type SwitchAction = 'select' | 'cancel' | 'next' | 'previous' | 'back' | 'up' | 'down' | 'left' | 'right';

interface UseExternalSwitchInputProps {
  onSelect?: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onBack?: () => void;
  onCancel?: () => void;
  onUp?: () => void;
  onDown?: () => void;
  onLeft?: () => void;
  onRight?: () => void;
  enabled?: boolean;
}

export function useExternalSwitchInput({
  onSelect,
  onNext,
  onPrevious,
  onBack,
  onCancel,
  onUp,
  onDown,
  onLeft,
  onRight,
  enabled = true,
}: UseExternalSwitchInputProps) {
  const { inputMethod } = useInputMethod();
  
  const isExternalSwitch = inputMethod === InputMethod.EXTERNAL_SWITCH;
  
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Skip when user is typing in an input field
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;

    if (!enabled || !isExternalSwitch) return;
    
    const settings = getUserSettings();
    const mappings = settings.externalSwitchMappings || {};
    
    // Find which action this key corresponds to
    for (const [actionId, mapping] of Object.entries(mappings) as [SwitchAction, ExternalSwitchMapping][]) {
      if (mapping.code === e.code || mapping.key === e.key) {
        e.preventDefault();
        e.stopPropagation();
        
        switch (actionId) {
          case 'select':
            onSelect?.();
            break;
          case 'cancel':
            onCancel?.();
            break;
          case 'next':
            onNext?.();
            break;
          case 'previous':
            onPrevious?.();
            break;
          case 'back':
            onBack?.();
            break;
          case 'up':
            onUp?.();
            break;
          case 'down':
            onDown?.();
            break;
          case 'left':
            onLeft?.();
            break;
          case 'right':
            onRight?.();
            break;
        }
        return;
      }
    }
  }, [enabled, isExternalSwitch, onSelect, onCancel, onNext, onPrevious, onBack, onUp, onDown, onLeft, onRight]);
  
  useEffect(() => {
    if (!enabled || !isExternalSwitch) return;
    
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [enabled, isExternalSwitch, handleKeyDown]);
  
  return { isExternalSwitch };
}

/**
 * Get the mapped action for a key event
 */
export function getExternalSwitchAction(e: KeyboardEvent): SwitchAction | null {
  const settings = getUserSettings();
  const mappings = settings.externalSwitchMappings || {};
  
  for (const [actionId, mapping] of Object.entries(mappings) as [SwitchAction, ExternalSwitchMapping][]) {
    if (mapping.code === e.code || mapping.key === e.key) {
      return actionId;
    }
  }
  
  return null;
}
